import { useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { type ObjectType } from "store/appData";
import { type ImageAnnotation } from "../api";

const FALLBACK_BOX_COLOR = "#1976d2";

// Coordinates are normalised to [0, 1] (x/y = top-left corner). `annotations`
// is expected to be pre-filtered to the object types worth showing, with the
// type to display first.
export default function AnnotationOverlay({
  annotations,
  objectTypes,
  width,
  height,
}: {
  annotations: ImageAnnotation[];
  objectTypes: Record<number, ObjectType>;
  width: number;
  height: number;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  // Only the smallest box under the pointer shows its label, so overlapping
  // boxes don't drown each other out.
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        ref={overlayRef}
        data-testid="datasetImagePanel.annotationOverlay"
        onMouseMove={(event) => {
          const el = overlayRef.current;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) return;
          const px = (event.clientX - rect.left) / rect.width;
          const py = (event.clientY - rect.top) / rect.height;
          let best: ImageAnnotation | null = null;
          for (const a of annotations) {
            const inside =
              px >= a.x &&
              px <= a.x + a.width &&
              py >= a.y &&
              py <= a.y + a.height;
            if (!inside) continue;
            if (!best || a.width * a.height < best.width * best.height) best = a;
          }
          setHoveredId(best ? best.id : null);
        }}
        onMouseLeave={() => setHoveredId(null)}
        sx={{ position: "relative", width, height, pointerEvents: "auto" }}
      >
        {annotations.map((annotation) => {
          const objectTypeId = annotation.types[0]?.object_type_id;
          const objectType =
            objectTypeId != null ? objectTypes[objectTypeId] : undefined;
          const color = objectType?.color || FALLBACK_BOX_COLOR;
          const hovered = annotation.id === hoveredId;
          return (
            <Tooltip
              key={annotation.id}
              title={objectType?.en_name ?? ""}
              open={Boolean(hovered && objectType?.en_name)}
              placement="top"
              arrow
              disableHoverListener
              disableFocusListener
              disableTouchListener
            >
              <Box
                data-testid={`datasetImagePanel.annotation.${annotation.id}`}
                sx={{
                  position: "absolute",
                  left: `${100 * annotation.x}%`,
                  top: `${100 * annotation.y}%`,
                  width: `${100 * annotation.width}%`,
                  height: `${100 * annotation.height}%`,
                  border: `2px ${hovered ? "solid" : "dashed"} ${color}`,
                  boxSizing: "border-box",
                  pointerEvents: "none",
                  zIndex: hovered ? 1 : undefined,
                  transform: annotation.rotation
                    ? `rotate(${annotation.rotation}deg)`
                    : undefined,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
